import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  MapPin,
  Clock,
  Users,
  FileText,
  Info,
  ExternalLink,
  User,
  Coins,
  Camera,
  Crown,
  ThumbsUp,
  ThumbsDown,
  Flag,
  Check,
  X,
  AlertCircle,
  Calendar,
  ShoppingBag,
  MessageCircle,
  HelpCircle,
  Trash2,
  Unlock,
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { Order } from '../types/order';
import { formatDistanceToNow } from '../utils/dateUtils';
import { PhotoViewer } from './PhotoViewer';
import { ActionSheet } from './ActionSheet';
import { BuyCoinsModal } from './BuyCoinsModal';
import { CoinsBalance } from './CoinsBalance';
import { StandardTooltip } from './StandardTooltip';
import { UrgentBadge } from './UrgentBadge';
import { ExpressBadge } from './ExpressBadge';
import { ForYouBadge } from './ForYouBadge';
import { LikeBadge } from './LikeBadge';
import { DislikeBadge } from './DislikeBadge';
import { ReportBadge } from './ReportBadge';
import { OnboardingStep } from './OnboardingStepper';
import { ReportOrderScreen } from './ReportOrderScreen';
import { ReportConfirmationScreen } from './ReportConfirmationScreen';
import { useCallback, useEffect, useState } from 'react';
import { mockOrders } from '../data/mockOrders';
import { OffersService } from '@/services/store/offers';
import {
  IBuyOfferResponse,
  IGetOfferByIdRequest,
  IGetOfferByIdResponse,
  IRejectReason,
} from '@/services/store/interfaces/offers.interfaces';
import { parseOfferByIdToOrder } from '@/utils/store/parseOfferDetails';
import { ButtonLoading, ButtonLoadingBlue, LoadingScreen } from './Loading';
import { useRouter } from 'next/navigation';
import { showToast } from '@/components/atoms/Toast/Toast';
import { ClientContactInfo } from './ClientContactInfo';
import { OrderDetailsSkeleton } from './OrderDetailsSkeleton';
import { Skeleton } from './ui/skeleton';

// Hook para detectar tamanho da tela
function useWindowSize() {
  const [windowSize, setWindowSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 768,
    height: typeof window !== 'undefined' ? window.innerHeight : 1024,
  });

  useEffect(() => {
    function handleResize() {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    }

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return windowSize;
}

interface OrderDetailsModalProps {
  order: Order | null;
  isOpen: boolean;
  onClose: () => void;
  coinsBalance: number;
  onPurchaseCoins: (coinsAdded: number) => void;
  orderAction: 'like' | 'dislike' | 'dislike-removed' | 'report' | null;
  onSetOrderAction: (
    orderId: string,
    action: 'like' | 'dislike' | 'dislike-removed' | 'report' | null
  ) => void;
  onboardingStep?: OnboardingStep;
  onOpenCoinPackages?: () => void;
  onSelectOrder?: (order: Order) => void;
  authToken: string;
  shouldValidateDocument: boolean;
  validationUrl: string;
  handleDisplayErrorNotFound: (status: number) => void;
  onOfferBought?: (orderId: string) => void;
  onOfferRemoved?: (orderId: string) => void;
}

// DETALHES DO PEDIDO
export function OrderDetailsModal({
  order,
  isOpen,
  onClose,
  coinsBalance,
  onPurchaseCoins,
  orderAction,
  onSetOrderAction,
  onboardingStep = 4,
  onOpenCoinPackages,
  onSelectOrder,
  authToken,
  handleDisplayErrorNotFound,
  onOfferBought,
  onOfferRemoved,
}: OrderDetailsModalProps) {
  const router = useRouter();
  const { width } = useWindowSize();
  const isMobile = width < 768;

  const [photoViewerOpen, setPhotoViewerOpen] = useState(false);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(0);
  const [showActionSheet, setShowActionSheet] = useState(false);
  const [showBuyCoinsModal, setShowBuyCoinsModal] = useState(false);
  const [showInsufficientCoinsModal, setShowInsufficientCoinsModal] =
    useState(false);
  const [targetUnlockPrice, setTargetUnlockPrice] = useState<number>(0);
  const [showReportSheet, setShowReportSheet] = useState(false);
  const [showReportConfirmation, setShowReportConfirmation] = useState(false);
  const [showFeedbackTooltip, setShowFeedbackTooltip] = useState(false);
  const [showDislikeConfirmModal, setShowDislikeConfirmModal] = useState(false);
  const [selectedDislikeReason, setSelectedDislikeReason] = useState<
    string | null
  >(null);
  const [dislikeDetails, setDislikeDetails] = useState('');
  const [dislikeReasons, setDislikeReasons] = useState<IRejectReason[]>([]);
  const [loadingDislikeReasons, setLoadingDislikeReasons] = useState(false);
  const [submittingDislike, setSubmittingDislike] = useState(false);

  const [purchaseConfirmation, setPurchaseConfirmation] = useState<{
    price: number;
    onConfirm: () => void;
  } | null>(null);

  const [apiOrder, setApiOrder] = useState<Order | null>(null);
  const [rawApiResponse, setRawApiResponse] =
    useState<IGetOfferByIdResponse | null>(null);
  const [isOrderBought, setIsOrderBought] = useState(false);
  const [orderClientInfo, setOrderClientInfo] = useState<IBuyOfferResponse>();
  const [isLoading, setIsLoading] = useState(false);
  const [loadingBuyOffer, setLoadingBuyOffer] = useState(false);
  const [loadingBuyExclusive, setLoadingBuyExclusive] = useState(false);

  const handleReadOffer = async () => {
    if (!order) return;

    if (order.isViewed) return;

    await OffersService.readOffers({
      offerId: order.id,
      authToken: authToken,
    }).catch((error) => handleDisplayErrorNotFound(error.response.status));
  };

  const getOfferDetails = useCallback(
    async ({ offerId, authToken }: IGetOfferByIdRequest) => {
      setIsLoading(true);
      setIsOrderBought(false);

      await OffersService.getOfferById({
        offerId,
        authToken,
      })
        .then((res) => {
          const parsed = parseOfferByIdToOrder({
            offerId,
            response: res,
          });

          setApiOrder(parsed);
          setRawApiResponse(res);
          setIsLoading(false);
        })
        .catch((error) => handleDisplayErrorNotFound(error.response.status));
    },
    [order?.id]
  );

  useEffect(() => {
    if (!isOpen) return;
    if (!order) return;

    const requestId = order.id.split('-')[1];

    void handleReadOffer();
    void getOfferDetails({ offerId: requestId, authToken });
  }, [isOpen, order]);

  const getDislikeReasons = useCallback(async () => {
    setLoadingDislikeReasons(true);

    await OffersService.getRejectReasons({ authToken })
      .then((res) => setDislikeReasons(res.reasons ?? []))
      .catch(() =>
        showToast({
          type: 'error',
          message: 'Erro ao carregar os motivos',
        })
      )
      .finally(() => setLoadingDislikeReasons(false));
  }, [authToken]);

  // Busca os motivos de "não gostei" ao abrir o modal de confirmação.
  useEffect(() => {
    if (!showDislikeConfirmModal) return;
    if (dislikeReasons.length > 0) return;

    void getDislikeReasons();
  }, [showDislikeConfirmModal, dislikeReasons.length, getDislikeReasons]);

  /**
   * Chamada de API para rejeitar pedido (reject order).
   * O reject sempre remove o pedido da loja do profissional.
   * POST {BFF}/offers/{requestId}/reject — usa apenas o id do pedido.
   */
  const rejectOrder = useCallback(
    async (reason: string, details?: string) => {
      if (!order) return;

      const requestId = order.id.split('-')[1];

      try {
        await OffersService.rejectOffer({
          authToken,
          requestId,
          reason,
          details,
        });
      } catch (error) {
        // Mantém a UX atual mesmo em caso de erro na chamada de API.
        // Loga no console para facilitar debugging em ambiente de desenvolvimento.
        // eslint-disable-next-line no-console
        console.error('Erro ao rejeitar pedido (reject order):', error);
      }
    },
    [order, authToken]
  );

  if (isLoading) {
    return (
      <>
        {/* Backdrop para telas maiores */}
        {!isMobile && (
          <motion.div
            style={{
              position: 'fixed',
              top: 0,
              right: 0,
              bottom: 0,
              left: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 49,
              backdropFilter: 'blur(4px)',
              WebkitBackdropFilter: 'blur(4px)',
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          />
        )}
        <OrderDetailsSkeleton isMobile={isMobile} />
      </>
    );
  }

  if (!apiOrder) return null;

  const {
    id,
    customerName,
    customerInitials,
    category,
    serviceType,
    description,
    createdAt,
    distance,
    unlockedCount,
    maxUnlocks,
    isUrgent,
    isHighlighted,
    isRecommended,
    location,
    questionsAndAnswers,
    photos,
    clientInfo,
    coinCost,
  } = apiOrder;

  const selectedReason = dislikeReasons.find(
    (reason) => reason.key === selectedDislikeReason
  );
  const needsDislikeDetails = selectedReason?.need_details ?? false;
  const canSubmitDislike =
    Boolean(selectedDislikeReason) &&
    (!needsDislikeDetails || dislikeDetails.trim().length > 0);

  const isFirstToUnlock = unlockedCount === 0;
  const unlockPercentage = (unlockedCount / maxUnlocks) * 100;
  const exclusiveCoinCost = coinCost + 100; // Exclusive is always +100 coins
  const missingCoins = Math.max(0, coinCost - coinsBalance);

  // CTA Logic Variables
  const hasBeenUnlocked = !isFirstToUnlock; // At least one pro unlocked
  const priceNormal = coinCost;
  const priceExclusive = exclusiveCoinCost;
  const userCoins = coinsBalance;
  const hasEnoughForNormal = userCoins >= priceNormal;
  const hasEnoughForExclusive = userCoins >= priceExclusive;

  // Navigation
  const currentIndex = mockOrders.findIndex((o) => o.id === id);
  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex < mockOrders.length - 1;

  const handlePhotoClick = (index: number) => {
    setSelectedPhotoIndex(index);
    setPhotoViewerOpen(true);
  };

  const openNavigation = (lat: number, lng: number) => {
    const url = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
    window.open(url, '_blank');
  };

  const handlePrevious = () => {
    if (hasPrevious && onSelectOrder) {
      const prevOrder = mockOrders[currentIndex - 1];
      onSelectOrder(prevOrder);
    }
  };

  const handleNext = () => {
    if (hasNext && onSelectOrder) {
      const nextOrder = mockOrders[currentIndex + 1];
      onSelectOrder(nextOrder);
    }
  };

  // Redireciona para a loja de moedas. O parâmetro `back_to=store` sinaliza
  // que o usuário veio do fluxo de saldo insuficiente, fazendo a loja de
  // moedas exibir um botão de "voltar para a loja de pedidos".
  const handleGoToCoinsStore = () => {
    // Leva o id do pedido aberto para que, ao voltar da loja de moedas, ele
    // seja reaberto (e não apenas a listagem).
    const offerParam = order ? `&offer_id=${order.id}` : '';

    router.push(
      `/seja-um-profissional/pagamentos/ios?auth_token=${authToken}&back_to=store${offerParam}`
    );
  };

  const handleBuyOffer = async () => {
    if (!order) return null;

    if (!hasEnoughForNormal) {
      setShowInsufficientCoinsModal(true);
      return;
    }

    setLoadingBuyOffer(true);

    await OffersService.buyOffer({
      authToken: authToken,
      requestId: order.id,
    })
      .then((res) => {
        setIsOrderBought(true);
        setOrderClientInfo(res);
        setLoadingBuyOffer(false);
      })
      .catch(() =>
        showToast({
          type: 'error',
          message: 'Erro ao liberar pedido',
        })
      );
  };

  const handelBuyOfferWithExclusivity = async () => {
    if (!order) return null;

    if (!hasEnoughForExclusive) {
      setShowInsufficientCoinsModal(true);
      return;
    }

    setLoadingBuyExclusive(true);

    await OffersService.buyOfferWithExclusivity({
      authToken: authToken,
      requestId: order.id,
    })
      .then((res) => {
        setIsOrderBought(true);
        setOrderClientInfo(res);
        setLoadingBuyExclusive(false);
      })
      .catch(() =>
        showToast({
          type: 'error',
          message: 'Erro ao liberar pedido',
        })
      );
  };

  const requestPurchaseConfirmation = (
    price: number,
    onConfirm: () => void
  ) => {
    // Se não houver saldo suficiente, abre o modal de moedas insuficientes
    // antes do modal de confirmação de compra.
    if (coinsBalance < price) {
      setShowInsufficientCoinsModal(true);
      return;
    }

    setPurchaseConfirmation({ price, onConfirm });
  };

  const handleConfirmPurchase = () => {
    if (!purchaseConfirmation) return;

    const { onConfirm } = purchaseConfirmation;
    setPurchaseConfirmation(null);
    onConfirm();
  };

  // Calcular largura máxima responsiva
  const getModalMaxWidth = () => {
    if (width < 768) {
      // Mobile: 100% da largura
      return '100%';
    } else if (width < 1024) {
      // Tablet: 600px
      return '600px';
    } else if (width < 1440) {
      // Desktop pequeno: 700px
      return '700px';
    } else {
      // Desktop grande: 800px
      return '800px';
    }
  };

  const modalMaxWidth = getModalMaxWidth();

  return (
    <AnimatePresence mode="wait">
      {isOpen && (
        <>
          {/* Backdrop para telas maiores */}
          {!isMobile && (
            <motion.div
              style={{
                position: 'fixed',
                top: 0,
                right: 0,
                bottom: 0,
                left: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                zIndex: 49,
                backdropFilter: 'blur(4px)',
                WebkitBackdropFilter: 'blur(4px)',
              }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={onClose}
            />
          )}

          <motion.div
            style={{
              position: 'fixed',
              top: 0,
              right: isMobile ? 0 : 'auto',
              bottom: 0,
              left: isMobile ? 0 : '50%',
              transform: isMobile ? 'none' : 'translateX(-50%)',
              backgroundColor: '#ffffff',
              zIndex: 50,
              display: 'flex',
              flexDirection: 'column',
              maxWidth: modalMaxWidth,
              width: isMobile ? '100%' : modalMaxWidth,
              marginLeft: isMobile ? 0 : 'auto',
              marginRight: isMobile ? 0 : 'auto',
              boxShadow: isMobile ? 'none' : '0 20px 60px rgba(0, 0, 0, 0.3)',
            }}
            initial={{
              x: isMobile ? '100%' : 0,
              opacity: isMobile ? 1 : 0,
              scale: isMobile ? 1 : 0.95,
            }}
            animate={{ x: 0, opacity: 1, scale: 1 }}
            exit={{
              x: isMobile ? '100%' : 0,
              opacity: isMobile ? 1 : 0,
              scale: isMobile ? 1 : 0.95,
            }}
            transition={{
              type: 'spring',
              stiffness: 400,
              damping: 35,
              duration: 0.25,
            }}
          >
            {/* Header - Fixed */}
            <header
              style={{
                position: 'sticky',
                top: 0,
                zIndex: 20,
                backgroundColor: '#ffffff',
                borderBottom: '1px solid #e5e7eb',
                marginBottom: 5,
              }}
            >
              {/* Back Button Row with Coins Balance */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  paddingLeft: '20px',
                  paddingRight: '20px',
                  paddingTop: '12px',
                  paddingBottom: '12px',
                }}
              >
                <motion.button
                  onClick={() => {
                    if (isOrderBought && onOfferBought && order) {
                      onOfferBought(order.id);
                    }
                    onClose();
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    color: '#4b5563',
                    transition: 'color 150ms ease',
                    cursor: 'pointer',
                    borderWidth: 0,
                    background: 'transparent',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = '#111827';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = '#4b5563';
                  }}
                  whileTap={{ scale: 0.95 }}
                >
                  <ArrowLeft
                    className="w-5 h-5"
                    style={{ width: '20px', height: '20px' }}
                  />
                  <span
                    style={{
                      fontSize: '0.875rem', // text-sm
                      lineHeight: '1.25rem',
                      fontWeight: 500, // font-medium
                    }}
                  >
                    Voltar
                  </span>
                </motion.button>

                {/* Coins Balance (Right side) */}
                {/*<CoinsBalance
                  balance={coinsBalance}
                  onboardingStep={onboardingStep}
                  onClick={onOpenCoinPackages}
                />*/}
              </div>
            </header>

            {/* Content - Scrollable */}
            <div
              style={{
                flex: 1, // flex-1
                overflowY: 'auto', // overflow-y-auto
              }}
            >
              {/* Hero Section */}
              <motion.section
                style={{
                  paddingTop: '20px',
                  paddingLeft: '20px',
                  paddingRight: '20px',
                  paddingBottom: '0',
                }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
              >
                {/* Title (Customer Name) and Timer */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: '16px', // gap-4
                    marginBottom: '4px', // mb-1
                  }}
                >
                  <h1
                    className="text-foreground"
                    style={{
                      fontSize: '20px',
                      fontWeight: 'var(--font-weight-semibold)',
                      lineHeight: '1.3',
                    }}
                  >
                    {customerName}
                  </h1>
                  {/* Timer (aligned with name baseline) */}
                  {/*<div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px', // gap-1.5
                      flexShrink: 0, // shrink-0
                      fontSize: '14px',
                      color: '#556377',
                      paddingTop: '2px',
                    }}
                  >
                    <Clock
                      className="w-4 h-4"
                      style={{ width: '16px', height: '16px' }}
                    />
                    <span>{formatDistanceToNow(createdAt)}</span>
                  </div>*/}
                </div>

                {/* Subtitle (Category + Type) */}
                <p
                  className="text-card-foreground"
                  style={{
                    fontSize: '16px',
                    fontWeight: 'var(--font-weight-medium)',
                    lineHeight: '1.4',
                    marginBottom: '8px',
                  }}
                >
                  {category}
                </p>

                {/* Badges */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px', // gap-2
                    flexWrap: 'wrap',
                  }}
                >
                  {isRecommended && <ForYouBadge />}
                  {isHighlighted && <ExpressBadge />}
                  {isUrgent && <UrgentBadge />}
                  {orderAction === 'like' && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{
                        duration: 0.15,
                        ease: 'easeOut',
                      }}
                    >
                      <LikeBadge />
                    </motion.div>
                  )}
                  {orderAction === 'dislike' && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{
                        duration: 0.15,
                        ease: 'easeOut',
                      }}
                    >
                      <DislikeBadge />
                    </motion.div>
                  )}
                  {orderAction === 'dislike-removed' && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{
                        duration: 0.15,
                        ease: 'easeOut',
                      }}
                    >
                      <DislikeBadge />
                    </motion.div>
                  )}
                  {orderAction === 'report' && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{
                        duration: 0.15,
                        ease: 'easeOut',
                      }}
                    >
                      <ReportBadge />
                    </motion.div>
                  )}
                </div>
              </motion.section>

              {/* Description */}
              {description && (
                <motion.section
                  style={{
                    padding: '24px',
                  }}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.2 }}
                >
                  <p
                    style={{
                      fontSize: '16px',
                      lineHeight: '26px',
                      color: '#101828',
                      fontWeight: 'var(--font-weight-normal)',
                    }}
                  >
                    {description}
                  </p>
                </motion.section>
              )}

              {/* Gallery */}
              {photos && photos.length > 0 && (
                <motion.section
                  style={{
                    backgroundColor: 'white',
                    position: 'relative',
                  }}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.25 }}
                >
                  <h3
                    style={{
                      fontSize: '16px',
                      fontWeight: 'var(--font-weight-semibold)',
                      color: 'var(--foreground)',
                      paddingLeft: '24px',
                      paddingTop: '16px',
                      paddingBottom: '12px',
                    }}
                  >
                    Fotos
                  </h3>

                  <div
                    className="scrollbar-hide"
                    style={{
                      display: 'flex',
                      gap: '12px', // gap-3
                      overflowX: 'auto', // overflow-x-auto
                      paddingLeft: '24px', // pl-6
                    }}
                  >
                    {photos.map((photo, index) => (
                      <motion.button
                        key={index}
                        onClick={() => handlePhotoClick(index)}
                        className=""
                        style={{
                          flexShrink: 0, // flex-shrink-0
                          overflow: 'hidden',
                          backgroundColor: '#f3f4f6', // bg-gray-100
                          transition: 'opacity 150ms ease', // transition-opacity
                          cursor: 'pointer',
                          width: '128px',
                          height: '128px',
                          borderRadius: '8px',
                          border: 'none',
                          padding: 0,
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.opacity = '0.9';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.opacity = '1';
                        }}
                        onMouseDown={(e) => {
                          e.currentTarget.style.opacity = '0.75';
                        }}
                        onMouseUp={(e) => {
                          e.currentTarget.style.opacity = '0.9';
                        }}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{
                          delay: 0.3 + index * 0.05,
                        }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <img
                          src={photo}
                          alt={`Foto ${index + 1}`}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            display: 'block',
                          }}
                        />
                      </motion.button>
                    ))}
                  </div>

                  <div style={{ height: '24px' }} />
                </motion.section>
              )}

              {/* Location & Client Info - Combined Section */}
              <div style={{ padding: '24px' }}>
                {/* Location Card */}
                <motion.div
                  style={{
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    padding: '24px',
                    boxShadow: '0px 20px 50px 10px rgba(10, 33, 94, 0.12)',
                    border: '1px solid #E5E7EB',
                    marginBottom: '24px',
                  }}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '10px', // gap-2.5
                    }}
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{
                        flexShrink: 0, // flex-shrink-0
                        color: 'var(--foreground)',
                        marginTop: '2px',
                      }}
                    >
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                      <circle cx="12" cy="10" r="3" />
                    </svg>

                    <div
                      style={{
                        flex: 1, // flex-1
                        minWidth: 0, // min-w-0
                      }}
                    >
                      <p
                        style={{
                          fontSize: '16px',
                          fontWeight: 'var(--font-weight-medium)',
                          color: 'var(--foreground)',
                          marginBottom: '2px',
                          lineHeight: '24px',
                        }}
                      >
                        {location.city}, {location.state}
                      </p>
                      <p
                        style={{
                          fontSize: '14px',
                          fontWeight: 'var(--font-weight-normal)',
                          color: '#556377',
                          lineHeight: '21px',
                          marginBottom: '0',
                        }}
                      >
                        {distance.toFixed(1)} km de você
                      </p>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: '12px',
                        }}
                      >
                        <p
                          style={{
                            fontSize: '14px',
                            fontWeight: 'var(--font-weight-normal)',
                            color: '#556377',
                            lineHeight: '21px',
                            margin: 0,
                          }}
                        >
                          {serviceType}
                        </p>
                        {/* Botão "Abrir mapa" desativado por ora — usamos a
                            prévia do mapa (RadiusMap) logo abaixo. */}
                        {/* {serviceType === "Presencial" &&
                          rawApiResponse?.tip?.geolocation &&
                          rawApiResponse.tip.geolocation.latitude &&
                          rawApiResponse.tip.geolocation.longitude && (
                            <motion.button
                              onClick={() =>
                                openNavigation(
                                  rawApiResponse.tip.geolocation.latitude,
                                  rawApiResponse.tip.geolocation.longitude,
                                )
                              }
                              className="inline-flex items-center justify-center transition-all"
                              style={{
                                fontSize: "14px",
                                fontWeight: "var(--font-weight-medium)",
                                color: "var(--foreground)",
                                backgroundColor: "white",
                                paddingLeft: "13px",
                                paddingRight: "13px",
                                paddingTop: "7px",
                                paddingBottom: "7px",
                                borderRadius: "10px",
                                minHeight: "35px",
                                border: "1px solid var(--border)",
                                cursor: "pointer",
                                flexShrink: 0,
                              }}
                              whileTap={{ scale: 0.96 }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor =
                                  "var(--muted)";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = "white";
                              }}
                              aria-label="Abrir localização no mapa"
                            >
                              Abrir mapa
                            </motion.button>
                          )} */}
                      </div>
                    </div>
                  </div>

                  {/* Prévia da localização: área aproximada (raio), sem ponto exato */}
                  {serviceType === 'Presencial' &&
                    rawApiResponse?.tip?.geolocation &&
                    rawApiResponse.tip.geolocation.latitude &&
                    rawApiResponse.tip.geolocation.longitude && (
                      <RadiusMap
                        lat={rawApiResponse.tip.geolocation.latitude}
                        lng={rawApiResponse.tip.geolocation.longitude}
                      />
                    )}
                </motion.div>

                {/* Client Information Card */}
                {clientInfo && (
                  <motion.div
                    style={{
                      backgroundColor: 'white',
                      borderRadius: '12px',
                      padding: '24px',
                      boxShadow: '0px 20px 50px 10px rgba(10, 33, 94, 0.12)',
                      border: '1px solid #E5E7EB',
                    }}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.33 }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        gap: '24px',
                      }}
                    >
                      {/* Left Side - Name */}
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          flex: 1,
                        }}
                      >
                        <h3
                          style={{
                            fontSize: '16px',
                            fontWeight: 'var(--font-weight-semibold)',
                            color: 'var(--foreground)',
                            lineHeight: '22.4px',
                          }}
                        >
                          {customerName}
                        </h3>
                      </div>

                      {/* Right Side - Info Items */}
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '24px',
                        }}
                      >
                        {/* Member Since */}
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '8px',
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: '36px',
                              height: '36px',
                              borderRadius: '18px',
                              backgroundColor: '#ececf0',
                            }}
                          >
                            <Calendar
                              className="w-4 h-4"
                              style={{
                                width: '16px',
                                height: '16px',
                                color: '#5a5a6b',
                              }}
                            />
                          </div>
                          <p
                            style={{
                              fontSize: '12px',
                              fontWeight: 'var(--font-weight-semibold)',
                              color: 'var(--foreground)',
                              lineHeight: '19.2px',
                            }}
                          >
                            Cliente desde {clientInfo.memberSince}
                          </p>
                        </div>

                        {/* Completed Requests */}
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '8px',
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: '36px',
                              height: '36px',
                              borderRadius: '18px',
                              backgroundColor: '#ececf0',
                            }}
                          >
                            <ShoppingBag
                              className="w-4 h-4"
                              style={{
                                width: '16px',
                                height: '16px',
                                color: '#5a5a6b',
                              }}
                            />
                          </div>
                          <p
                            style={{
                              fontSize: '12px',
                              fontWeight: 'var(--font-weight-semibold)',
                              color: 'var(--foreground)',
                              lineHeight: '19.2px',
                            }}
                          >
                            {clientInfo.completedRequests}{' '}
                            {clientInfo.completedRequests === 1
                              ? 'pedido realizado'
                              : 'pedidos realizados'}
                          </p>
                        </div>

                        {/* WhatsApp Status */}
                        {/* V1 Release - Commented out for future use
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '8px',
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: '36px',
                              height: '36px',
                              borderRadius: '18px',
                              backgroundColor: clientInfo.hasWhatsApp
                                ? 'rgba(22, 163, 74, 0.1)'
                                : clientInfo?.whatsAppStatus === 'pending'
                                  ? 'rgba(234, 179, 8, 0.1)'
                                  : '#ececf0',
                            }}
                          >
                            {clientInfo.hasWhatsApp ? (
                              <MessageCircle
                                className="w-4 h-4"
                                style={{
                                  width: '16px',
                                  height: '16px',
                                  color: '#16a34a',
                                }}
                              />
                            ) : clientInfo?.whatsAppStatus === 'pending' ? (
                              <AlertCircle
                                className="w-4 h-4"
                                style={{
                                  width: '16px',
                                  height: '16px',
                                  color: '#eab308',
                                }}
                              />
                            ) : (
                              <X
                                className="w-4 h-4"
                                style={{
                                  width: '16px',
                                  height: '16px',
                                  color: '#5a5a6b',
                                }}
                              />
                            )}
                          </div>
                          <p
                            style={{
                              fontSize: '12px',
                              fontWeight: 'var(--font-weight-semibold)',
                              color: clientInfo.hasWhatsApp
                                ? '#16a34a'
                                : clientInfo?.whatsAppStatus === 'pending'
                                  ? '#eab308'
                                  : 'var(--foreground)',
                              lineHeight: '19.2px',
                            }}
                          >
                            {clientInfo.hasWhatsApp
                              ? 'Disponível via WhatsApp'
                              : clientInfo?.whatsAppStatus === 'pending'
                                ? 'Aguardando verificação'
                                : 'WhatsApp indisponível'}
                          </p>
                        </div>
                        */}
                      </div>
                    </div>
                  </motion.div>
                )}
              </div>

              {/* Request Details - Improved Q&A spacing */}
              {questionsAndAnswers && questionsAndAnswers.length > 0 && (
                <motion.section
                  style={{
                    padding: '24px',
                    paddingTop: '32px',
                  }}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.37 }}
                >
                  <h3
                    style={{
                      fontSize: '16px',
                      fontWeight: 'var(--font-weight-semibold)',
                      marginBottom: '24px',
                      color: 'var(--foreground)',
                      lineHeight: '24px',
                    }}
                  >
                    Detalhes do pedido
                  </h3>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px',
                    }}
                  >
                    {questionsAndAnswers.map((qa, index) => (
                      <motion.div
                        key={index}
                        style={{
                          backgroundColor: '#f6f8fa',
                          borderRadius: '8px',
                          padding: '24px',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '4px',
                          alignItems: 'flex-end',
                        }}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{
                          delay: 0.4 + index * 0.03,
                        }}
                      >
                        {/* Question with number */}
                        <div
                          style={{
                            display: 'flex',
                            gap: '6px',
                            alignItems: 'flex-start',
                            width: '100%',
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '10px',
                              height: '100%',
                              alignItems: 'center',
                              width: '10px',
                              flexShrink: 0,
                            }}
                          >
                            <span
                              style={{
                                fontSize: '14px',
                                color: '#556377',
                                lineHeight: '21px',
                                fontWeight: 'var(--font-weight-black)',
                                alignSelf: 'flex-start',
                              }}
                            >
                              {index + 1}.
                            </span>
                          </div>
                          <div
                            style={{
                              fontSize: '14px',
                              color: '#556377',
                              lineHeight: '1.4',
                              fontWeight: 'var(--font-weight-bold)',
                              flex: 1,
                            }}
                          >
                            {qa.question}
                          </div>
                        </div>

                        {/* Answer with left border */}
                        <div
                          style={{
                            width: '100%',
                            borderLeft: '1px solid #E1E6E9',
                            padding: '12px 16px',
                          }}
                        >
                          <p
                            style={{
                              fontSize: '16px',
                              color: '#020817',
                              lineHeight: '1.4',
                              fontWeight: 'var(--font-weight-normal)',
                              margin: 0,
                            }}
                          >
                            {qa.answer}
                          </p>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </motion.section>
              )}

              {/* Rate This Order Section - REBUILT */}
              <motion.section
                style={{
                  padding: '24px',
                  paddingTop: '32px',
                }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.42 }}
              >
                {/* Title with Subtitle */}
                <div style={{ marginBottom: '12px' }}>
                  <h3
                    style={{
                      fontSize: '16px',
                      fontWeight: 'var(--font-weight-semibold)',
                      color: 'var(--foreground)',
                      marginBottom: '12px',
                      lineHeight: '24px',
                    }}
                  >
                    O que você achou deste pedido?
                  </h3>
                  <p
                    style={{
                      fontSize: '12px',
                      fontWeight: 'var(--font-weight-normal)',
                      lineHeight: '18px',
                      color: '#556377',
                    }}
                  >
                    Isso ajuda a personalizar recomendações para você. Você
                    também pode filtrar os pedidos da loja para ver apenas os
                    que você gostou.
                  </p>
                </div>

                {/* Like/Dislike Buttons Row */}
                <div className="flex gap-3 mb-4">
                  {/* Like Button */}
                  {/* <motion.button
                    onClick={() =>
                      onSetOrderAction(
                        id,
                        orderAction === 'like' ? null : 'like'
                      )
                    }
                    className="flex-1 flex items-center justify-center gap-2 transition-all"
                    style={{
                      minHeight: '48px',
                      paddingTop: '12px',
                      paddingBottom: '12px',
                      paddingLeft: '16px',
                      paddingRight: '16px',
                      borderRadius: 'var(--radius-button)',
                      border:
                        orderAction === 'like'
                          ? 'none'
                          : '1.5px solid var(--border)',
                      backgroundColor:
                        orderAction === 'like'
                          ? 'var(--success-soft)'
                          : 'transparent',
                    }}
                    whileTap={{ scale: 0.95 }}
                    animate={
                      orderAction === 'like'
                        ? {
                            scale: [1, 1.02, 1],
                            opacity: [0.8, 1],
                          }
                        : {}
                    }
                    transition={{
                      duration: 0.15,
                      ease: [0.25, 0.1, 0.25, 1],
                    }}
                  >
                    <motion.div
                      animate={
                        orderAction === 'like' ? { scale: [0.9, 1.05, 1] } : {}
                      }
                      transition={{
                        duration: 0.3,
                        ease: 'easeOut',
                      }}
                    >
                      <ThumbsUp
                        className="w-5 h-5"
                        style={{
                          stroke:
                            orderAction === 'like'
                              ? 'var(--success)'
                              : 'var(--muted-foreground)',
                          fill:
                            orderAction === 'like' ? 'var(--success)' : 'none',
                          strokeWidth: '2',
                        }}
                      />
                    </motion.div>
                    <span
                      style={{
                        fontSize: 'var(--text-sm)',
                        fontWeight: 'var(--font-weight-semibold)',
                        color:
                          orderAction === 'like'
                            ? 'var(--success)'
                            : 'var(--muted-foreground)',
                      }}
                    >
                      Gostei
                    </span>
                  </motion.button> */}

                  {/* Dislike Button */}
                  <motion.button
                    onClick={() => {
                      if (
                        orderAction === 'dislike' ||
                        orderAction === 'dislike-removed'
                      ) {
                        // Toggle off if already selected
                        onSetOrderAction(id, null);
                      } else {
                        // Show confirmation modal before applying
                        setShowDislikeConfirmModal(true);
                      }
                    }}
                    className="flex-1 flex items-center justify-center gap-2 transition-all"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      width: '100%',
                      minHeight: '48px',
                      paddingTop: '12px',
                      paddingBottom: '12px',
                      paddingLeft: '16px',
                      paddingRight: '16px',
                      borderRadius: 'var(--radius-button)',
                      border:
                        orderAction === 'dislike' ||
                        orderAction === 'dislike-removed'
                          ? 'none'
                          : '1.5px solid var(--border)',
                      backgroundColor:
                        orderAction === 'dislike' ||
                        orderAction === 'dislike-removed'
                          ? 'var(--destructive-soft)'
                          : 'transparent',
                    }}
                    whileTap={{ scale: 0.95 }}
                    animate={
                      orderAction === 'dislike' ||
                      orderAction === 'dislike-removed'
                        ? {
                            scale: [1, 1.02, 1],
                            opacity: [0.8, 1],
                          }
                        : {}
                    }
                    transition={{
                      duration: 0.15,
                      ease: [0.25, 0.1, 0.25, 1],
                    }}
                  >
                    <motion.div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                      animate={
                        orderAction === 'dislike' ||
                        orderAction === 'dislike-removed'
                          ? { scale: [0.9, 1.05, 1] }
                          : {}
                      }
                      transition={{
                        duration: 0.3,
                        ease: 'easeOut',
                      }}
                    >
                      <ThumbsDown
                        className="w-5 h-5"
                        style={{
                          width: '20px',
                          height: '20px',
                          stroke:
                            orderAction === 'dislike' ||
                            orderAction === 'dislike-removed'
                              ? 'var(--destructive)'
                              : 'var(--muted-foreground)',
                          fill:
                            orderAction === 'dislike' ||
                            orderAction === 'dislike-removed'
                              ? 'var(--destructive)'
                              : 'none',
                          strokeWidth: '2',
                        }}
                      />
                    </motion.div>
                    <span
                      style={{
                        fontSize: 'var(--text-sm)',
                        fontWeight: 'var(--font-weight-semibold)',
                        color:
                          orderAction === 'dislike' ||
                          orderAction === 'dislike-removed'
                            ? 'var(--destructive)'
                            : 'var(--muted-foreground)',
                      }}
                    >
                      Não gostei
                    </span>
                  </motion.button>
                </div>

                {/* Report Link — "Denunciar pedido" desativado por ora.
                {orderAction !== 'report' ? (
                  <motion.button
                    onClick={() => setShowReportSheet(true)}
                    className="w-full flex items-center justify-center gap-1.5 transition-colors"
                    style={{
                      marginTop: '8px',
                      fontSize: 'var(--text-sm)',
                      fontWeight: 'var(--font-weight-normal)',
                      color: 'var(--muted-foreground)',
                      textDecoration: 'underline',
                      paddingTop: 0,
                      paddingBottom: 0,
                      backgroundColor: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                    }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Flag className="w-3.5 h-3.5" />
                    Denunciar pedido
                  </motion.button>
                ) : (
                  <div
                    className="w-full text-center"
                    style={{
                      fontSize: 'var(--text-sm)',
                      color: 'var(--destructive)',
                      paddingTop: '4px',
                      paddingBottom: '4px',
                    }}
                  >
                    <span
                      style={{
                        fontWeight: 'var(--font-weight-medium)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                      }}
                    >
                      <Flag
                        className="w-4 h-4"
                        style={{
                          stroke: 'var(--destructive)',
                          strokeWidth: '2',
                          fill: 'var(--destructive)',
                        }}
                      />
                      Você denunciou este pedido
                    </span>
                  </div>
                )}
                */}
              </motion.section>

              {/* Extra spacing for fixed footer */}
              <div style={{ height: '16px' }} />
            </div>

            {/* Footer CTA - Fixed */}
            <motion.div
              style={{
                position: 'sticky',
                bottom: 0,
                zIndex: 20,
                backgroundColor: '#ffffff',
                borderTop: '1px solid #e5e7eb',
                paddingLeft: '24px', // px-6
                paddingRight: '24px',
                // paddingTop: '16px', // py-4
                paddingBottom: '16px',
                boxShadow: '0 -4px 12px rgba(0,0,0,0.08)',
              }}
              initial={{ y: 100, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{
                delay: 0.3,
                type: 'spring',
                stiffness: 200,
                damping: 25,
              }}
            >
              {/* Unlock Status with Progress Bar and Tooltip */}
              {isOrderBought && orderClientInfo && clientInfo ? (
                <ClientContactInfo
                  customerName={orderClientInfo.tip.customer}
                  clientInfo={{
                    email: orderClientInfo.tip.email,
                    hasWhatsApp: clientInfo.hasWhatsApp,
                    phoneNumber: orderClientInfo.tip.phone,
                    whatsappMessage: orderClientInfo.tip.whatsapp_message,
                  }}
                  unlockedOrderInfo={{
                    isExclusive: false,
                    unlockedAt: Date(),
                  }}
                  order={{
                    unlockedCount: 1,
                  }}
                />
              ) : (
                <>
                  <div>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: '4px',
                        position: 'relative',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          fontSize: '0.875rem', // text-sm
                          lineHeight: '1.25rem',
                          color: '#6b7280', // text-gray-500
                        }}
                      >
                        <Users
                          className="w-4 h-4"
                          style={{ width: '16px', height: '16px' }}
                        />
                        <span>
                          {isFirstToUnlock
                            ? 'Nenhum profissional liberou ainda'
                            : unlockedCount === 1
                              ? '1 profissional já liberou'
                              : `${unlockedCount} de ${maxUnlocks} profissionais já liberaram`}
                        </span>
                      </div>

                      <div
                        style={{
                          position: 'relative',
                          flexShrink: 0,
                          minWidth: '40px',
                          minHeight: '40px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          visibility: isFirstToUnlock ? 'visible' : 'hidden',
                        }}
                      >
                        {isFirstToUnlock && (
                          <StandardTooltip
                            content="Ninguém liberou este pedido ainda, por isso ele pode ser exclusivo seu."
                            position="top"
                          >
                            <button
                              style={{
                                flexShrink: 0,
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '32px',
                                height: '32px',
                                borderRadius: '50%',
                                border: 'none',
                                backgroundColor: 'transparent',
                                cursor: 'pointer',
                                opacity: 0.7,
                                transition:
                                  'opacity 0.2s ease, background-color 0.2s ease',
                                padding: '0',
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.opacity = '1';
                                e.currentTarget.style.backgroundColor =
                                  'rgba(0, 0, 0, 0.05)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.opacity = '0.7';
                                e.currentTarget.style.backgroundColor =
                                  'transparent';
                              }}
                              aria-label="Informações sobre pedido exclusivo"
                            >
                              {/*<Info
                              style={{
                                width: '18px',
                                height: '18px',
                                color: '#6b7280',
                              }}
                            />*/}
                            </button>
                          </StandardTooltip>
                        )}
                      </div>
                    </div>

                    {/* Progress Bar */}
                    <div
                      style={{
                        width: '100%',
                        height: '0.5rem', // h-2
                        backgroundColor: '#f3f4f6', // bg-gray-100
                        borderRadius: '9999px', // rounded-full
                        overflow: 'hidden',
                        marginBottom: '1rem',
                      }}
                    >
                      <motion.div
                        style={{
                          height: '100%',
                          borderRadius: '9999px',
                          transition: 'background-color 150ms ease',
                          backgroundColor:
                            unlockPercentage === 0
                              ? '#e5e7eb' // gray-200
                              : unlockPercentage < 50
                                ? '#60a5fa' // blue-400
                                : unlockPercentage < 80
                                  ? '#facc15' // yellow-400
                                  : '#fb923c', // orange-400
                        }}
                        initial={{ width: 0 }}
                        animate={{ width: `${unlockPercentage}%` }}
                        transition={{
                          duration: 0.6,
                          ease: 'easeOut',
                        }}
                      />
                    </div>
                  </div>

                  {/* CTAs - 5 Scenarios Logic */}
                  {orderAction !== 'report' &&
                    (() => {
                      const applyHoverActiveBg = (
                        e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
                        color: string
                      ) => {
                        e.currentTarget.style.backgroundColor = color;
                      };

                      // Scenario A: Already unlocked + has enough coins
                      if (hasBeenUnlocked) {
                        return (
                          <motion.button
                            style={{
                              width: '100%',
                              borderRadius: '12px', // rounded-xl
                              color: '#ffffff',
                              backgroundColor: '#2563eb', // blue-600
                              transition: 'background-color 150ms ease',
                              padding: '14px 20px',
                              border: 'none',
                              cursor: 'pointer',
                            }}
                            onMouseEnter={(e) =>
                              applyHoverActiveBg(e, '#1d4ed8')
                            } // blue-700
                            onMouseLeave={(e) =>
                              applyHoverActiveBg(e, '#2563eb')
                            } // blue-600
                            onMouseDown={(e) =>
                              applyHoverActiveBg(e, '#1e40af')
                            } // blue-800
                            onMouseUp={(e) => applyHoverActiveBg(e, '#1d4ed8')} // back to hover
                            whileTap={{ scale: 0.98 }}
                            onClick={() =>
                              requestPurchaseConfirmation(
                                priceNormal,
                                handleBuyOffer
                              )
                            }
                            disabled={loadingBuyOffer}
                          >
                            {loadingBuyOffer ? (
                              <ButtonLoadingBlue />
                            ) : (
                              <div
                                style={{
                                  fontSize: 'var(--text-base)',
                                  fontWeight: 'var(--font-weight-semibold)',
                                  lineHeight: '1.4',
                                }}
                              >
                                Liberar por {priceNormal} moedas
                              </div>
                            )}
                          </motion.button>
                        );
                      }

                      // Scenario B: Not unlocked + has enough for both
                      if (!hasBeenUnlocked) {
                        return (
                          <div
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '8px',
                            }}
                          >
                            {/* Primary: Exclusive CTA */}
                            <motion.button
                              style={{
                                width: '100%',
                                borderRadius: '12px',
                                color: '#ffffff',
                                backgroundColor: '#10b981', // emerald-500
                                transition: 'background-color 150ms ease',
                                padding: '14px 20px',
                                border: 'none',
                                cursor: 'pointer',
                                textAlign: 'center',
                              }}
                              onMouseEnter={(e) =>
                                applyHoverActiveBg(e, '#059669')
                              } // emerald-600
                              onMouseLeave={(e) =>
                                applyHoverActiveBg(e, '#10b981')
                              } // emerald-500
                              onMouseDown={(e) =>
                                applyHoverActiveBg(e, '#047857')
                              } // emerald-700
                              onMouseUp={(e) =>
                                applyHoverActiveBg(e, '#059669')
                              }
                              whileTap={{ scale: 0.98 }}
                              onClick={() =>
                                requestPurchaseConfirmation(
                                  priceExclusive,
                                  handelBuyOfferWithExclusivity
                                )
                              }
                              disabled={loadingBuyExclusive}
                            >
                              {loadingBuyExclusive ? (
                                <ButtonLoading />
                              ) : (
                                <div
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '12px',
                                  }}
                                >
                                  <Crown
                                    style={{
                                      width: '18px',
                                      height: '18px',
                                      flexShrink: 0,
                                    }}
                                  />
                                  <div
                                    style={{
                                      fontSize: 'var(--text-base)',
                                      fontWeight: 'var(--font-weight-semibold)',
                                      lineHeight: '1.4',
                                    }}
                                  >
                                    Liberar como exclusivo por {priceExclusive}
                                  </div>
                                </div>
                              )}
                            </motion.button>

                            {/* Secondary: Normal CTA */}
                            <motion.button
                              style={{
                                width: '100%',
                                borderRadius: '12px',
                                transition: 'background-color 150ms ease',
                                padding: '14px 20px',
                                backgroundColor: 'var(--background)',
                                border: '1px solid var(--border)',
                                cursor: 'pointer',
                                textAlign: 'center',
                              }}
                              whileTap={{ scale: 0.98 }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor =
                                  'var(--muted)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor =
                                  'var(--background)';
                              }}
                              onClick={() =>
                                requestPurchaseConfirmation(
                                  priceNormal,
                                  handleBuyOffer
                                )
                              }
                              disabled={loadingBuyOffer}
                            >
                              {loadingBuyOffer ? (
                                <ButtonLoadingBlue />
                              ) : (
                                <div
                                  style={{
                                    fontSize: 'var(--text-base)',
                                    fontWeight: 'var(--font-weight-semibold)',
                                    lineHeight: '1.4',
                                  }}
                                >
                                  Liberar por {priceNormal} moedas
                                </div>
                              )}
                            </motion.button>
                          </div>
                        );
                      }
                      return null;
                    })()}
                </>
              )}
            </motion.div>
          </motion.div>

          {/* Photo Viewer */}
          {photos && photos.length > 0 && (
            <PhotoViewer
              photos={photos}
              initialIndex={selectedPhotoIndex}
              isOpen={photoViewerOpen}
              onClose={() => setPhotoViewerOpen(false)}
            />
          )}

          {/* Action Sheet */}
          <ActionSheet
            isOpen={showActionSheet}
            onClose={() => setShowActionSheet(false)}
            onLike={() => {
              onSetOrderAction(id, 'like');
              setShowActionSheet(false);
            }}
            onDislike={() => {
              onSetOrderAction(id, 'dislike');
              setShowActionSheet(false);
            }}
            onReport={() => {
              setShowActionSheet(false);
              setShowReportSheet(true);
            }}
          />

          {/* Report Order Screen */}
          <ReportOrderScreen
            isOpen={showReportSheet}
            onClose={() => setShowReportSheet(false)}
            onSubmit={(reason) => {
              void rejectOrder(reason);
              onSetOrderAction(id, 'report');
              setShowReportSheet(false);
              setShowReportConfirmation(true);
              setTimeout(() => {
                setShowReportConfirmation(false);
              }, 2500);
            }}
            customerName={customerName}
            category={category}
          />

          {/* Report Confirmation Screen */}
          <ReportConfirmationScreen
            isOpen={showReportConfirmation}
            onClose={() => setShowReportConfirmation(false)}
          />

          {/* Buy Coins Modal */}
          <BuyCoinsModal
            isOpen={showBuyCoinsModal}
            onClose={() => setShowBuyCoinsModal(false)}
            onPurchaseComplete={onPurchaseCoins}
            missingCoins={missingCoins}
            currentBalance={coinsBalance}
            orderPrice={targetUnlockPrice}
          />

          {/* Insufficient Coins Modal */}
          <AnimatePresence>
            {showInsufficientCoinsModal && (
              <>
                {/* Backdrop */}
                <motion.div
                  style={{
                    position: 'fixed',
                    top: 0,
                    right: 0,
                    bottom: 0,
                    left: 0,
                    backgroundColor: '#000000',
                    zIndex: 60,
                  }}
                  onClick={() => setShowInsufficientCoinsModal(false)}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 0.5 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                />

                {/* Modal */}
                <motion.div
                  style={{
                    position: 'fixed',
                    top: 0,
                    right: 0,
                    bottom: 0,
                    left: 0,
                    zIndex: 70,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '24px',
                  }}
                  onClick={() => setShowInsufficientCoinsModal(false)}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <motion.div
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      width: '100%',
                      maxWidth: '448px',
                      backgroundColor: 'var(--card)',
                      borderRadius: 'var(--radius-card)',
                      padding: '24px',
                      boxShadow: 'var(--elevation-lg)',
                    }}
                    initial={{ scale: 0.95, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.95, opacity: 0 }}
                    transition={{
                      type: 'spring',
                      stiffness: 300,
                      damping: 25,
                    }}
                  >
                    {/* Icon */}
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '56px',
                        height: '56px',
                        borderRadius: '50%',
                        backgroundColor: 'var(--kojo-blue-subtle)',
                        marginBottom: '16px',
                      }}
                    >
                      <Coins
                        style={{
                          width: '28px',
                          height: '28px',
                          color: 'var(--kojo-blue)',
                        }}
                      />
                    </div>

                    {/* Title */}
                    <h2
                      style={{
                        fontSize: 'var(--text-xl)',
                        fontWeight: 'var(--font-weight-bold)',
                        color: 'var(--foreground)',
                        marginBottom: '8px',
                        lineHeight: '1.4',
                      }}
                    >
                      Moedas insuficientes
                    </h2>

                    {/* Message */}
                    <p
                      style={{
                        fontSize: 'var(--text-base)',
                        fontWeight: 'var(--font-weight-normal)',
                        color: 'var(--muted-foreground)',
                        lineHeight: '1.5',
                        marginBottom: '24px',
                      }}
                    >
                      Você não tem moedas suficientes para liberar este pedido.
                      Compre mais moedas para continuar.
                    </p>

                    {/* Actions */}
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                      }}
                    >
                      <motion.button
                        onClick={handleGoToCoinsStore}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px',
                          width: '100%',
                          minHeight: '48px',
                          padding: '14px 16px',
                          borderRadius: 'var(--radius-card)',
                          border: 'none',
                          backgroundColor: '#2563eb',
                          color: '#ffffff',
                          fontSize: 'var(--text-base)',
                          fontWeight: 'var(--font-weight-semibold)',
                          cursor: 'pointer',
                        }}
                        whileTap={{ scale: 0.98 }}
                      >
                        Comprar moedas
                      </motion.button>

                      <motion.button
                        onClick={() => setShowInsufficientCoinsModal(false)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '100%',
                          minHeight: '48px',
                          padding: '14px 16px',
                          borderRadius: 'var(--radius-card)',
                          border: '1px solid var(--border)',
                          backgroundColor: 'var(--background)',
                          color: 'var(--foreground)',
                          fontSize: 'var(--text-base)',
                          fontWeight: 'var(--font-weight-semibold)',
                          cursor: 'pointer',
                        }}
                        whileTap={{ scale: 0.98 }}
                      >
                        Agora não
                      </motion.button>
                    </div>
                  </motion.div>
                </motion.div>
              </>
            )}
          </AnimatePresence>

          {/* Dislike Confirmation Modal */}
          <AnimatePresence>
            {showDislikeConfirmModal && (
              <>
                {/* Backdrop */}
                <motion.div
                  style={{
                    position: 'fixed',
                    top: 0,
                    right: 0,
                    bottom: 0,
                    left: 0,
                    backgroundColor: '#000000',
                    zIndex: 60,
                  }}
                  onClick={() => setShowDislikeConfirmModal(false)}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 0.5 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                />

                {/* Modal */}
                <motion.div
                  style={{
                    position: 'fixed',
                    top: 0,
                    right: 0,
                    bottom: 0,
                    left: 0,
                    zIndex: 70,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '24px',
                  }}
                  onClick={() => setShowDislikeConfirmModal(false)}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <motion.div
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      width: '100%',
                      maxWidth: '448px',
                      backgroundColor: 'var(--card)',
                      borderRadius: 'var(--radius-card)',
                      padding: '24px',
                      boxShadow: 'var(--elevation-lg)',
                    }}
                    initial={{ scale: 0.95, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.95, opacity: 0 }}
                    transition={{
                      type: 'spring',
                      stiffness: 300,
                      damping: 25,
                    }}
                  >
                    {/* Title */}
                    <h2
                      style={{
                        fontSize: 'var(--text-xl)',
                        fontWeight: 'var(--font-weight-bold)',
                        color: 'var(--foreground)',
                        marginBottom: '24px',
                        lineHeight: '1.5',
                      }}
                    >
                      Por que você não gostou?
                    </h2>

                    {/* Radio Options */}
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '12px',
                        marginBottom: '24px',
                      }}
                    >
                      {loadingDislikeReasons
                        ? Array.from({ length: 6 }).map((_, index) => (
                            <Skeleton
                              key={index}
                              delay={index * 60}
                              style={{
                                height: '58px',
                                width: '100%',
                                borderRadius: 'var(--radius-card)',
                              }}
                            />
                          ))
                        : dislikeReasons.map((option) => (
                            <label
                              key={option.key}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                cursor: 'pointer',
                                gap: '16px',
                                padding: '16px',
                                borderRadius: 'var(--radius-card)',
                                border:
                                  selectedDislikeReason === option.key
                                    ? '2px solid var(--kojo-blue)'
                                    : '1px solid var(--border)',
                                backgroundColor:
                                  selectedDislikeReason === option.key
                                    ? 'var(--kojo-blue-subtle)'
                                    : 'transparent',
                                transition: 'all 0.15s ease',
                              }}
                              onMouseEnter={(e) => {
                                if (selectedDislikeReason !== option.key) {
                                  e.currentTarget.style.backgroundColor =
                                    'rgba(0,0,0,0.03)';
                                }
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor =
                                  selectedDislikeReason === option.key
                                    ? 'var(--kojo-blue-subtle)'
                                    : 'transparent';
                              }}
                              onClick={() =>
                                setSelectedDislikeReason(option.key)
                              }
                            >
                              <span
                                style={{
                                  fontSize: 'var(--text-base)',
                                  fontWeight: 'var(--font-weight-medium)',
                                  color: 'var(--foreground)',
                                  lineHeight: '1.5',
                                  flex: 1,
                                }}
                              >
                                {option.name}
                              </span>

                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  flexShrink: 0,
                                  transition: 'all 0.15s ease',
                                  width: '24px',
                                  height: '24px',
                                  borderRadius: '50%',
                                  backgroundColor:
                                    selectedDislikeReason === option.key
                                      ? 'var(--kojo-blue)'
                                      : 'transparent',
                                  border:
                                    selectedDislikeReason === option.key
                                      ? 'none'
                                      : '2px solid var(--border)',
                                }}
                              >
                                {selectedDislikeReason === option.key && (
                                  <motion.div
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    transition={{
                                      type: 'spring',
                                      stiffness: 500,
                                      damping: 30,
                                    }}
                                  >
                                    <Check
                                      style={{
                                        width: '16px',
                                        height: '16px',
                                        color: 'var(--background)',
                                        strokeWidth: '3px',
                                        marginTop: '6px',
                                      }}
                                    />
                                  </motion.div>
                                )}
                              </div>
                            </label>
                          ))}
                    </div>

                    {/* Campo de detalhes (quando o motivo selecionado exige) */}
                    {needsDislikeDetails && (
                      <textarea
                        value={dislikeDetails}
                        onChange={(e) => setDislikeDetails(e.target.value)}
                        placeholder="Conte um pouco mais (obrigatório)"
                        rows={3}
                        style={{
                          width: '100%',
                          resize: 'vertical',
                          padding: '12px 16px',
                          borderRadius: 'var(--radius-card)',
                          border: '1px solid var(--border)',
                          backgroundColor: 'var(--background)',
                          color: 'var(--foreground)',
                          fontSize: '14',
                          fontWeight: 'var(--font-weight-normal)',
                          lineHeight: '1.5',
                          marginBottom: '24px',
                        }}
                      />
                    )}

                    {/* Remove from Store Checkbox
                        Removido: o endpoint de reject sempre remove o pedido
                        da loja, então a opção não é mais necessária.
                    <label
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '12px',
                        cursor: 'pointer',
                        margin: '24px 0px',
                      }}
                      onClick={() => setRemoveFromStore(!removeFromStore)}
                    >
                      <div
                        style={{
                          width: '20px',
                          height: '20px',
                          borderRadius: 'var(--radius-badge)',
                          border: `2px solid ${
                            removeFromStore
                              ? 'var(--destructive)'
                              : 'var(--border)'
                          }`,
                          backgroundColor: removeFromStore
                            ? 'var(--destructive)'
                            : 'transparent',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          marginTop: '2px',
                          transition: 'all 0.15s ease',
                        }}
                      >
                        {removeFromStore && (
                          <Check
                            style={{
                              width: '14px',
                              height: '14px',
                              stroke: 'var(--destructive-foreground)',
                              strokeWidth: '3',
                            }}
                          />
                        )}
                      </div>
                      <span
                        style={{
                          fontSize: 'var(--text-base)',
                          fontWeight: 'var(--font-weight-medium)',
                          color: 'var(--foreground)',
                          lineHeight: '1.5',
                        }}
                      >
                        Remover este pedido da minha loja
                      </span>
                    </label>
                    */}

                    <div style={{ marginBottom: '24px' }} />

                    {/* Submit Button */}
                    <motion.button
                      onClick={() => {
                        if (
                          !canSubmitDislike ||
                          !selectedDislikeReason ||
                          submittingDislike
                        ) {
                          return;
                        }

                        const reason = selectedDislikeReason;
                        const details = needsDislikeDetails
                          ? dislikeDetails.trim()
                          : undefined;
                        const removedId = order?.id;

                        // Simula um pequeno carregamento no botão para o envio
                        // não ser instantâneo.
                        setSubmittingDislike(true);
                        void rejectOrder(reason, details);

                        setTimeout(() => {
                          // O reject sempre remove o pedido da loja. Removemos
                          // da listagem atual (sem nova chamada à API) e
                          // voltamos para a lista na posição anterior.
                          if (removedId) {
                            onOfferRemoved?.(removedId);
                          }

                          setSubmittingDislike(false);
                          setSelectedDislikeReason(null);
                          setDislikeDetails('');

                          // Fecha primeiro o modal de "não gostei" e, após a
                          // animação de saída, fecha os detalhes do pedido —
                          // para os dois não sumirem fora de ordem.
                          setShowDislikeConfirmModal(false);
                          setTimeout(() => {
                            onClose();
                          }, 300);
                        }, 1500);
                      }}
                      disabled={!canSubmitDislike || submittingDislike}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        transition: 'all 150ms ease',
                        width: '100%',

                        minHeight: '48px',
                        paddingTop: '14px',
                        paddingBottom: '14px',
                        paddingLeft: '16px',
                        paddingRight: '16px',
                        borderRadius: 'var(--radius-card)',
                        border: 'none',
                        backgroundColor: canSubmitDislike
                          ? 'var(--destructive)'
                          : 'var(--muted)',
                        color: canSubmitDislike
                          ? 'var(--destructive-foreground)'
                          : 'var(--muted-foreground)',
                        fontSize: 'var(--text-base)',
                        fontWeight: 'var(--font-weight-semibold)',
                        cursor:
                          canSubmitDislike && !submittingDislike
                            ? 'pointer'
                            : 'not-allowed',
                        opacity: canSubmitDislike ? 1 : 0.6,
                      }}
                      whileTap={
                        canSubmitDislike && !submittingDislike
                          ? { scale: 0.95 }
                          : {}
                      }
                      transition={{
                        duration: 0.12,
                        ease: 'easeOut',
                      }}
                    >
                      {submittingDislike ? (
                        <ButtonLoading />
                      ) : (
                        <>
                          <ThumbsDown
                            style={{
                              width: '16px',
                              height: '16px',
                              stroke: canSubmitDislike
                                ? 'var(--destructive-foreground)'
                                : 'var(--muted-foreground)',
                              strokeWidth: '2',
                            }}
                          />
                          Enviar avaliação
                        </>
                      )}
                    </motion.button>
                  </motion.div>
                </motion.div>
              </>
            )}
          </AnimatePresence>

          {/* Purchase Confirmation Modal */}
          <AnimatePresence>
            {purchaseConfirmation && (
              <>
                {/* Backdrop */}
                <motion.div
                  style={{
                    position: 'fixed',
                    top: 0,
                    right: 0,
                    bottom: 0,
                    left: 0,
                    backgroundColor: '#000000',
                    zIndex: 60,
                  }}
                  onClick={() => setPurchaseConfirmation(null)}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 0.5 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                />

                {/* Modal */}
                <motion.div
                  style={{
                    position: 'fixed',
                    top: 0,
                    right: 0,
                    bottom: 0,
                    left: 0,
                    zIndex: 70,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '24px',
                  }}
                  onClick={() => setPurchaseConfirmation(null)}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <motion.div
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      width: '100%',
                      maxWidth: '400px',
                      backgroundColor: 'var(--card)',
                      borderRadius: 'var(--radius-card)',
                      padding: '24px',
                      boxShadow: 'var(--elevation-lg)',
                      position: 'relative',
                    }}
                    initial={{ scale: 0.95, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.95, opacity: 0 }}
                    transition={{
                      type: 'spring',
                      stiffness: 300,
                      damping: 25,
                    }}
                  >
                    {/* Close button */}
                    <button
                      onClick={() => setPurchaseConfirmation(null)}
                      aria-label="Fechar"
                      style={{
                        position: 'absolute',
                        top: '16px',
                        right: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        border: 'none',
                        backgroundColor: 'transparent',
                        cursor: 'pointer',
                        color: 'var(--muted-foreground)',
                      }}
                    >
                      <X style={{ width: '20px', height: '20px' }} />
                    </button>

                    {/* Lock icon */}
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        marginTop: '12px',
                        marginBottom: '20px',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '64px',
                          height: '64px',
                        }}
                      >
                        <Unlock
                          style={{
                            width: '40px',
                            height: '40px',
                            color: '#facc15',
                            strokeWidth: 2,
                          }}
                        />
                      </div>
                    </div>

                    {/* Title */}
                    <h2
                      style={{
                        fontSize: 'var(--text-xl)',
                        fontWeight: 'var(--font-weight-bold)',
                        color: 'var(--foreground)',
                        textAlign: 'center',
                        marginBottom: '12px',
                        lineHeight: '1.4',
                      }}
                    >
                      Você usará {purchaseConfirmation.price} moedas para
                      liberar este contato.
                    </h2>

                    {/* Subtitle */}
                    <p
                      style={{
                        fontSize: 'var(--text-sm)',
                        fontWeight: 'var(--font-weight-normal)',
                        color: 'var(--muted-foreground)',
                        textAlign: 'center',
                        lineHeight: '1.5',
                        marginBottom: '24px',
                      }}
                    >
                      Use suas moedas para liberar o contato e negociar o
                      serviço direto com o cliente.
                    </p>

                    {/* Confirm Button */}
                    <motion.button
                      onClick={handleConfirmPurchase}
                      whileTap={{ scale: 0.98 }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                        minHeight: '48px',
                        padding: '14px 16px',
                        borderRadius: 'var(--radius-card)',
                        border: 'none',
                        backgroundColor: '#2563eb',
                        color: '#ffffff',
                        fontSize: 'var(--text-base)',
                        fontWeight: 'var(--font-weight-semibold)',
                        cursor: 'pointer',
                        marginBottom: '8px',
                      }}
                    >
                      Liberar contato
                    </motion.button>

                    {/* Cancel Button */}
                    <motion.button
                      onClick={() => setPurchaseConfirmation(null)}
                      whileTap={{ scale: 0.98 }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                        minHeight: '40px',
                        padding: '8px 16px',
                        borderRadius: 'var(--radius-card)',
                        border: 'none',
                        backgroundColor: 'transparent',
                        color: '#2563eb',
                        fontSize: 'var(--text-base)',
                        fontWeight: 'var(--font-weight-semibold)',
                        cursor: 'pointer',
                      }}
                    >
                      Agora não
                    </motion.button>
                  </motion.div>
                </motion.div>
              </>
            )}
          </AnimatePresence>
        </>
      )}
    </AnimatePresence>
  );
}

/**
 * Prévia que mostra apenas a ÁREA APROXIMADA do pedido (um raio), nunca a
 * localização exata do cliente.
 *
 * - As coordenadas são arredondadas (~1 km de imprecisão) para borrar o centro.
 * - Usa o embed do OpenStreetMap (sem chave de API) e NÃO adiciona marcador.
 * - O mapa é interativo (zoom/arraste). Por cima desenhamos um círculo
 *   translúcido representando o raio; ele e a legenda ficam sem captura de
 *   clique (pointerEvents: none) para não bloquear a interação. Como não há
 *   marcador e o centro é borrado, navegar no mapa não revela o ponto exato.
 */
function RadiusMap({ lat, lng }: { lat: number; lng: number }) {
  const round = (n: number) => Math.round(n * 100) / 100;
  const cLat = round(lat);
  const cLng = round(lng);

  // Meia-extensão da área visível, em km, em torno do centro borrado.
  const halfKm = 2;
  const dLat = halfKm / 111;
  const dLng = halfKm / (111 * Math.cos((cLat * Math.PI) / 180));

  // bbox do OSM: minLon,minLat,maxLon,maxLat
  const bbox = [cLng - dLng, cLat - dLat, cLng + dLng, cLat + dLat].join(',');
  const src = `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik`;

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        height: '180px',
        borderRadius: '12px',
        overflow: 'hidden',
        border: '1px solid var(--border)',
        marginTop: '12px',
      }}
    >
      <iframe
        title="Área aproximada do pedido"
        src={src}
        style={{
          width: '100%',
          height: '100%',
          border: 0,
        }}
        loading="lazy"
      />

      {/* Círculo translúcido representando o raio (área aproximada) */}
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          height: '50%',
          aspectRatio: '1 / 1',
          borderRadius: '50%',
          backgroundColor: 'rgba(37, 99, 235, 0.15)',
          border: '2px solid rgba(37, 99, 235, 0.6)',
          pointerEvents: 'none',
        }}
      />

      {/* Legenda */}
      <div
        style={{
          position: 'absolute',
          left: '8px',
          top: '8px',
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          borderRadius: '8px',
          padding: '4px 8px',
          fontSize: '12px',
          fontWeight: 'var(--font-weight-medium)',
          color: 'var(--foreground)',
          pointerEvents: 'none',
        }}
      >
        Área aproximada
      </div>
    </div>
  );
}
